import React, { useState, useMemo } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import { EnhancedDeal, ProposalBuilderData } from '../../types';
import { db } from '../../utils/firebase';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { X, Search, Loader2, Plus, Copy } from 'lucide-react';
import { NameProjectFields, NameProjectFieldsValue, reserveProjectNumberOnSubmit } from '../projects/NameProjectFields';
import {
  defaultClientLabelFromLead,
  defaultDescriptorFromProjectType,
  isProjectNumber,
} from '../../utils/projectName';
import { designIncludedInKitForNewProposalWrite } from '../../utils/proposalCalculations';
import './CopyProposalModal.css';

interface CopyProposalModalProps {
  open: boolean;
  onClose: () => void;
  sourceProposal: ProposalBuilderData;
  onCopied: (newDealId: string) => void;
}

type TargetMode = 'existing' | 'new';

const CopyProposalModal: React.FC<CopyProposalModalProps> = ({
  open,
  onClose,
  sourceProposal,
  onCopied,
}) => {
  const { leads, enhancedDeals, pipelines, createProjectForLead } = useCRM();
  const { user } = useAuth();

  const [targetMode, setTargetMode] = useState<TargetMode>('existing');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const [projectName, setProjectName] = useState(sourceProposal.projectName || '');
  const [clientName, setClientName] = useState(sourceProposal.clientName || '');
  const [location, setLocation] = useState(sourceProposal.location || '');
  // Naming convention state — only used when creating a brand-new project
  // for an existing lead (targetMode === 'new').
  const [naming, setNaming] = useState<NameProjectFieldsValue>({
    projectNumber: '',
    clientLabel: '',
    descriptor: sourceProposal.projectName || '',
  });
  const [namingError, setNamingError] = useState<string | null>(null);

  const [copying, setCopying] = useState(false);

  const activeLeads = useMemo(() =>
    leads.filter(l => !l.archived).sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    [leads]
  );

  const availableDeals = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return enhancedDeals
      .filter(d => {
        if (d.outcome !== 'active') return false;
        if (!q) return true;
        return (d.title?.toLowerCase().includes(q) || d.contactName?.toLowerCase().includes(q));
      })
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [enhancedDeals, searchQuery]);

  const dealsWithProposal = useMemo(() => {
    const set = new Set<string>();
    enhancedDeals.forEach(d => {
      if (d.id) set.add(d.id);
    });
    return set;
  }, [enhancedDeals]);

  const selectedLead = useMemo(() =>
    selectedLeadId ? leads.find(l => l.id === selectedLeadId) : null,
    [selectedLeadId, leads]
  );

  const handleSelectDeal = (deal: EnhancedDeal) => {
    setSelectedDealId(deal.id!);
    setClientName(deal.contactName || '');
    setLocation(deal.location || '');
    setProjectName(deal.projectName || deal.title?.split(' - ').slice(1).join(' - ') || '');
  };

  const handleSelectLead = (leadId: string) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    setSelectedLeadId(leadId);
    setClientName(`${lead.firstName} ${lead.lastName}`);
    setLocation(lead.location || '');
    setProjectName('');
    setNaming({
      projectNumber: '',
      clientLabel: defaultClientLabelFromLead(lead),
      descriptor: defaultDescriptorFromProjectType(sourceProposal.projectName || lead.projectType),
    });
    setNamingError(null);
  };

  const generateSlug = (client: string, project: string) => {
    return `${client}-${project}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const generatePin = () => Math.floor(1000 + Math.random() * 9000).toString();

  const handleCopy = async () => {
    setCopying(true);
    try {
      let targetDealId: string;

      if (targetMode === 'existing') {
        if (!selectedDealId) return;
        targetDealId = selectedDealId;
      } else {
        if (!selectedLeadId) return;
        if (!isProjectNumber(naming.projectNumber)) {
          setNamingError('Waiting for project number assignment. Please wait a moment and try again.');
          setCopying(false);
          return;
        }
        if (!naming.clientLabel.trim() || !naming.descriptor.trim()) {
          setNamingError('Client Label and Descriptor are required.');
          setCopying(false);
          return;
        }
        const lead = leads.find(l => l.id === selectedLeadId);
        if (!lead) return;
        const defaultPipeline = pipelines.find(p => p.isDefault) || pipelines[0];
        if (!defaultPipeline?.id) throw new Error('No pipeline found');
        // Reserve the actual project number atomically NOW (peek was preview).
        const reservedProjectNumber = await reserveProjectNumberOnSubmit();
        setNaming(prev => ({ ...prev, projectNumber: reservedProjectNumber }));
        // Use descriptor as legacy short name (fallbacks expect a string).
        targetDealId = await createProjectForLead(
          lead,
          naming.descriptor.trim(),
          sourceProposal.projectName || 'Custom',
          defaultPipeline.id,
          user?.id || 'system',
          undefined,
          {
            projectNumber: reservedProjectNumber,
            clientLabel: naming.clientLabel.trim(),
            descriptor: naming.descriptor.trim(),
          },
        );
        // Sync the proposal's projectName field with the descriptor so the
        // copied proposal has a sensible label for slug + display.
        if (!projectName.trim()) {
          setProjectName(naming.descriptor.trim());
        }
      }

      const existing = await getDoc(doc(db, 'proposalBuilders', targetDealId));
      if (existing.exists()) {
        if (!window.confirm('This deal already has a proposal. Overwrite it with the copied data?')) {
          setCopying(false);
          return;
        }
      }

      const newSlug = generateSlug(clientName, projectName || 'proposal');
      const newPin = generatePin();

      const copiedDoc: Record<string, any> = {
        id: targetDealId,
        dealId: targetDealId,
        clientName: clientName.trim(),
        leadCompany: sourceProposal.leadCompany || '',
        projectName: projectName.trim(),
        location: location.trim(),
        slug: newSlug,
        accessPin: newPin,
        nodeSets: JSON.parse(JSON.stringify(sourceProposal.nodeSets || [])),
        profitMargin: sourceProposal.profitMargin || 40,
        installOptional: sourceProposal.installOptional || false,
        // Explicit true/false on source wins; missing legacy field → new-create default.
        designIncludedInKit: designIncludedInKitForNewProposalWrite(sourceProposal.designIncludedInKit),
        isRomPricing: sourceProposal.isRomPricing || false,
        projectSquareFootage: sourceProposal.projectSquareFootage || undefined,
        publicSettings: sourceProposal.publicSettings
          ? { ...sourceProposal.publicSettings }
          : undefined,
        status: 'draft',
        checklist: sourceProposal.checklist || {},
        designTotal: sourceProposal.designTotal || 0,
        timberKitTotal: sourceProposal.timberKitTotal || 0,
        installTotal: sourceProposal.installTotal || 0,
        rawCostTotal: sourceProposal.rawCostTotal || 0,
        billableTotal: sourceProposal.billableTotal || 0,
        grossProfit: sourceProposal.grossProfit || 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: user?.id || 'system',
        globalCosts: sourceProposal.globalCosts || undefined,
      };

      if (sourceProposal.isKitProposal) {
        copiedDoc.isKitProposal = true;
        copiedDoc.sourceKitId = sourceProposal.sourceKitId;
        copiedDoc.sourceKitName = sourceProposal.sourceKitName;
        copiedDoc.kitAvailableSpecies = sourceProposal.kitAvailableSpecies;
        copiedDoc.offeredSpecies = sourceProposal.offeredSpecies;
        copiedDoc.kitPricing = sourceProposal.kitPricing;
      }

      // Remove undefined values
      Object.keys(copiedDoc).forEach(key => {
        if (copiedDoc[key] === undefined) delete copiedDoc[key];
      });

      await setDoc(doc(db, 'proposalBuilders', targetDealId), copiedDoc);

      onCopied(targetDealId);
      onClose();
    } catch (err) {
      console.error('Error copying proposal:', err);
      alert('Failed to copy proposal');
    } finally {
      setCopying(false);
    }
  };

  if (!open) return null;

  const canCopy = targetMode === 'existing'
    ? !!selectedDealId
    : !!selectedLeadId
        && isProjectNumber(naming.projectNumber)
        && !!naming.clientLabel.trim()
        && !!naming.descriptor.trim();

  return (
    <div className="cpm-overlay" onClick={onClose}>
      <div className="cpm-modal" onClick={e => e.stopPropagation()}>
        <div className="cpm-header">
          <Copy size={18} />
          <h3>Copy Proposal</h3>
          <button className="cpm-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="cpm-body">
          {/* Target mode toggle */}
          <div className="cpm-mode-toggle">
            <button
              className={`cpm-mode-btn ${targetMode === 'existing' ? 'active' : ''}`}
              onClick={() => setTargetMode('existing')}
            >
              Existing Deal
            </button>
            <button
              className={`cpm-mode-btn ${targetMode === 'new' ? 'active' : ''}`}
              onClick={() => setTargetMode('new')}
            >
              New Project
            </button>
          </div>

          {targetMode === 'existing' && (
            <div className="cpm-section">
              <label className="cpm-label">Select Target Deal</label>
              <div className="cpm-search">
                <Search size={14} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search deals..."
                  autoFocus
                />
              </div>
              <div className="cpm-deal-list">
                {availableDeals.slice(0, 20).map(deal => (
                  <button
                    key={deal.id}
                    className={`cpm-deal-item ${selectedDealId === deal.id ? 'selected' : ''}`}
                    onClick={() => handleSelectDeal(deal)}
                  >
                    <span className="cpm-deal-title">{deal.title}</span>
                    <span className="cpm-deal-contact">{deal.contactName}</span>
                  </button>
                ))}
                {availableDeals.length === 0 && (
                  <div className="cpm-empty">No matching deals found</div>
                )}
              </div>
            </div>
          )}

          {targetMode === 'new' && (
            <div className="cpm-section">
              <label className="cpm-label">Select Lead</label>
              <select
                className="cpm-select"
                value={selectedLeadId || ''}
                onChange={e => handleSelectLead(e.target.value)}
              >
                <option value="">Choose a lead...</option>
                {activeLeads.map(l => (
                  <option key={l.id} value={l.id}>{l.firstName} {l.lastName}</option>
                ))}
              </select>
              {selectedLeadId && (
                <div className="cpm-field" style={{ marginTop: 12 }}>
                  <label className="cpm-label">Project name</label>
                  <NameProjectFields
                    mode="create"
                    value={naming}
                    onChange={setNaming}
                    onError={setNamingError}
                    disabled={copying}
                  />
                  {namingError && <div className="cpm-empty">{namingError}</div>}
                </div>
              )}
            </div>
          )}

          {/* Editable fields */}
          {(selectedDealId || selectedLeadId) && (
            <div className="cpm-fields">
              <div className="cpm-field">
                <label className="cpm-label">Client Name</label>
                <input
                  type="text"
                  className="cpm-input"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                />
              </div>
              {targetMode === 'existing' && (
                <div className="cpm-field">
                  <label className="cpm-label">Project Name *</label>
                  <input
                    type="text"
                    className="cpm-input"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    placeholder="e.g., Pavilion, Carport"
                  />
                </div>
              )}
              <div className="cpm-field">
                <label className="cpm-label">Location</label>
                <input
                  type="text"
                  className="cpm-input"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="cpm-footer">
          <button className="cpm-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="cpm-btn-copy"
            onClick={handleCopy}
            disabled={copying || !canCopy}
          >
            {copying ? <><Loader2 size={14} className="spin" /> Copying...</> : <><Copy size={14} /> Copy & Open</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CopyProposalModal;
